import * as React from "react";

import type { ToastActionElement, ToastProps } from "@/components/Base/Toast";
import {
  AutomationToastStack,
  AutomationToastStackProps,
} from "@/components/Base/AutomationToastStack";
import { CustomerUpdateToast } from "@/components/Base/CustomerUpdateToast";
import { NotificationToast } from "@/components/Base/NotificationToast";
import {
  OverdueTaskToast,
  OverdueTaskToastProps,
} from "@/components/Base/OverdueTaskToast";
import { CustomerUpdateFormProps } from "@/components/CustomerUpdate/CustomerUpdateForm";
import { TOAST_PRIORITY } from "@/lib/toastEvictionCooldown";

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1;

type CustomToastComponent = React.ComponentType<any>;

interface CustomToast {
  component: CustomToastComponent;
  onOpenChange?: (open: boolean, reason?: "evicted") => void;
  props: Record<string, any>;
}

type ToasterToast = Omit<ToastProps, "onOpenChange"> & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  custom?: CustomToast;
  onOpenChange?: (open: boolean, reason?: "evicted") => void;
  ownerPriority?: number;
};

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

let count = 0;

/**
 *
 */
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | {
      type: ActionType["ADD_TOAST"];
      toast: ToasterToast;
    }
  | {
      type: ActionType["UPDATE_TOAST"];
      toast: Partial<ToasterToast>;
    }
  | {
      type: ActionType["DISMISS_TOAST"];
      toastId?: ToasterToast["id"];
    }
  | {
      type: ActionType["REMOVE_TOAST"];
      toastId?: ToasterToast["id"];
    };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      };

    case "DISMISS_TOAST": {
      const { toastId } = action;

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

/**
 * Toasts present in `previousToasts` but absent from `nextToasts` -- i.e.
 * what ADD_TOAST's TOAST_LIMIT slice just evicted. Pure and side-effect
 * free so it's directly testable without touching useToast's module-level
 * singleton state; dispatch() below is the only caller in production code.
 * @param {readonly T[]} previousToasts - Store contents before the action.
 * @param {readonly T[]} nextToasts - Store contents after the action.
 * @returns {T[]} The toasts that were evicted, in their original order.
 */
export function findEvictedToasts<
  T extends {
    id: string;
    onOpenChange?: (open: boolean, reason?: "evicted") => void;
  },
>(previousToasts: readonly T[], nextToasts: readonly T[]): T[] {
  const nextIds = new Set(nextToasts.map((t) => t.id));
  return previousToasts.filter((t) => !nextIds.has(t.id));
}

/**
 *
 * @param action
 */
function dispatch(action: Action) {
  const previousToasts = memoryState.toasts;
  memoryState = reducer(memoryState, action);

  // TOAST_LIMIT=1 means ADD_TOAST can silently slice an existing toast out
  // of state with no DISMISS_TOAST and no onOpenChange call. That toast's
  // own onOpenChange is the one reliable notification channel every manager
  // already listens to (see Toaster.tsx, which always routes a custom
  // toast's close through props.onOpenChange) -- call it directly here so
  // eviction is never silent, regardless of render timing.
  if (action.type === "ADD_TOAST") {
    findEvictedToasts(previousToasts, memoryState.toasts)
      // A toast already mid-close (DISMISS_TOAST fired, REMOVE_TOAST pending
      // on TOAST_REMOVE_DELAY) has already run its owner's close path.
      // Slicing it out here is the tail of a dismissal the owner itself
      // initiated, not a genuine eviction by another manager. Without this
      // filter, a manager's own dismiss-then-show-again sequence -- a common
      // pattern, see MyOverdueTaskToastManager's "render" branch and
      // AutomationToastManager's "show" branch -- misreports itself as
      // evicted, corrupting whatever state that manager's eviction-recovery
      // logic maintains.
      .filter((t) => t.open !== false)
      .forEach((t) => t.onOpenChange?.(false, "evicted"));
  }

  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

type Toast =
  | (Omit<ToasterToast, "id"> & {
      custom?: CustomToast;
      onOpenChange?: (open: boolean, reason?: "evicted") => void;
    })
  | CustomToast;

/**
 *
 * @param root0
 */
function toast({ ...props }: Toast) {
  const id = genId();

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });

  // Extract the custom onOpenChange if it exists
  const { onOpenChange: customOnOpenChange, ...otherProps } = props;

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...otherProps,
      id,
      open: true,
      onOpenChange: (open, reason) => {
        // Call custom onOpenChange first if it exists
        if (customOnOpenChange) {
          customOnOpenChange(open, reason);
        }
        // Then call the default dismiss behavior
        if (!open) dismiss();
      },
    },
  });

  return {
    id: id,
    dismiss,
    update,
  };
}

/**
 *
 */
function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),

    showNotification: (props: {
      type: "info" | "success" | "warning";
      title: string;
      message: string;
      actionText?: string;
      onAction?: () => void;
    }) => {
      return toast({
        custom: {
          component: NotificationToast,
          props,
        },
      });
    },

    showCustomerUpdate: (
      props: CustomerUpdateFormProps & {
        onDismiss?: () => void;
      }
    ) => {
      const toastInstance = toast({
        ownerPriority: TOAST_PRIORITY.CUSTOMER_UPDATE,
        onOpenChange: (open) => {
          if (!open) props.onDismiss?.();
        },
        custom: {
          component: CustomerUpdateToast,
          props: {
            ...props,
            onDismiss: () => toastInstance.dismiss(),
          },
        },
      });
      return toastInstance;
    },

    showTask: (props: OverdueTaskToastProps & { onEvicted?: () => void }) => {
      const toastInstance = toast({
        ownerPriority: TOAST_PRIORITY.OVERDUE_TASK,
        // Radix fires onOpenChange on every user-driven close (the X button and
        // — were auto-dismiss ever enabled — the timeout). Toaster.tsx overrides
        // the custom component's onDismiss with props.onOpenChange, so this is
        // the ONLY reliable hook to run the caller's reset (clearing the
        // manager's shownCountRef) when the toast closes. Without it a dismissed
        // toast leaves a stale count and can re-nag on the next count change.
        // `reason === "evicted"` (set only by dispatch()'s eviction-notify path,
        // see useToast.tsx's dispatch) is distinguished from a plain dismissal
        // because the two need different manager behavior: a dismissal should
        // not re-nag this session, but an eviction must, or the toast is lost
        // for good -- see MyOverdueTaskToastManager's onEvicted handler.
        onOpenChange: (open, reason) => {
          if (!open) {
            if (reason === "evicted") props.onEvicted?.();
            else props.onDismiss?.();
          }
        },
        custom: {
          component: OverdueTaskToast,
          props: {
            ...props,
            onDismiss: () => toastInstance.dismiss(),
          },
        },
      });
      return toastInstance;
    },

    showAutomationToasts: (
      props: AutomationToastStackProps & { onDismiss?: () => void }
    ) => {
      const toastInstance = toast({
        ownerPriority: TOAST_PRIORITY.AUTOMATION,
        onOpenChange: (open) => {
          if (!open) props.onDismiss?.();
        },
        custom: {
          component: AutomationToastStack,
          props: {
            ...props,
            onDismiss: () => toastInstance.dismiss(),
          },
        },
      });
      return toastInstance;
    },
  };
}

export { useToast, toast };
